// @flow
import * as React from 'react';

import {
  selectCartOwner,
  selectCanChooseResellee,
  selectCartReselleeID,
  selectIsFailureCartOwnerList,
  selectIsLoadedCartOwnerList,
  selectProductsByType,
} from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import type { TranslatorProps } from '@gandi/translate';

import {
  selectCanChooseProductOwner,
  selectCurrentOrganizationCanPay,
} from 'src/Cart/AccountChoice/modules/selectors';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { displaySirenMessage } from 'src/hooks/useMissingOrgInfo';
import { selectShopCurrent } from 'src/modules/organization/selectors';

import { useCartSummaryContext } from '../../../context/CartSummary';

function TRANSLATION_KEYS(__: TranslatorProps['__']): {
  [key:
    | 'cannotPay'
    | 'noOwnerOrg'
    | 'noProductOwner'
    | 'isReselleeWH'
    | 'noSiren']: string,
} {
  return {
    cannotPay: __('Payment cannot be processed'),
    noOwnerOrg: __('Please select an invoice address'),
    noProductOwner: __('Please select an owner address for the products'),
    isReselleeWH: __(
      'Please note that for web hosting products, the ownership remains to the reseller organization.',
    ),
    noSiren: __(
      'SIREN number missing, required as of September 1, 2026. Please edit the organization to add this information.',
    ),
  };
}

/**
 * Hook to display why the button is disabled
 * @param { canPay: boolean, ownerOrganization: boolean, hasOwnerOnly: boolean, productOwnerOrganization: boolean, cartOwnerIdIsReseller: boolean, resellee: boolean }
 * @returns { errorMessage: ?string, noWarning?: boolean }
 */
export function useButtonDisabledReason(): {
  errorMessage: ?string,
  noWarning?: boolean,
  isDisabled: boolean,
} {
  const { __ } = useTranslator();

  const cartOwner = useAppSelector(selectCartOwner);
  const {
    cartOwnerOrganization,
    productOwnerOrganization,
    sameOwnerCheckboxIsChecked,
  } = useCartSummaryContext();

  // TODO: Check if ownerOrganization is enough

  // WARN: we pass false to selectShopCurrent second param
  // because on AccountChoice at least
  // being authenticated does NOT mean that the current orga is loaded
  const currentShopOrganization = useAppSelector(state =>
    selectShopCurrent(state, false),
  );
  const reselleeId = useAppSelector(selectCartReselleeID);
  const canPay = useAppSelector(selectCurrentOrganizationCanPay);
  const chooseOwner = useAppSelector(selectCanChooseProductOwner);
  const isFailureCartOwners = useAppSelector(selectIsFailureCartOwnerList);
  const isLoadedCartOwners = useAppSelector(selectIsLoadedCartOwnerList);
  const chooseResellee = useAppSelector(state => {
    return currentShopOrganization?.reseller && selectCanChooseResellee(state);
  });

  const hasWebHosting = useAppSelector(
    state => selectProductsByType(state, { productType: 'paas' }).length > 0,
  );
  const displayNewAccountChoice = useFeatureFlag('FF_SHOP_ACCOUNT_CHOICE');

  const isLoading = !isLoadedCartOwners && !isFailureCartOwners;
  const cannotPay = !canPay;
  const noOwnerOrg = !(cartOwnerOrganization || cartOwner);
  const canChooseProductOwner = chooseOwner || chooseResellee;

  const noSiren =
    cartOwnerOrganization && displaySirenMessage(cartOwnerOrganization);
  // TODO: we need to check if the same owner checkbox is ticked as well
  // then it should set noProductOwner to false
  const noProductOwner =
    !sameOwnerCheckboxIsChecked &&
    canChooseProductOwner &&
    !productOwnerOrganization &&
    !reselleeId;

  const isDisabled = cannotPay || noOwnerOrg || !!noProductOwner;

  const isResellee = !!reselleeId;

  // eslint-disable-next-line new-cap
  const KEYS = TRANSLATION_KEYS(__);

  const { errorMessage, noWarning } = React.useMemo(() => {
    if (!displayNewAccountChoice || isLoading)
      return {
        errorMessage: null,
        errorKey: null,
      };

    if (cannotPay) {
      return {
        errorMessage: KEYS.cannotPay,
      };
    }

    if (noOwnerOrg) {
      return {
        errorMessage: KEYS.noOwnerOrg,
      };
    }

    if (noProductOwner) {
      return {
        errorMessage: KEYS.noProductOwner,
        noWarning: true,
      };
    }

    if (isResellee && hasWebHosting)
      return { errorMessage: KEYS.isReselleeWH, noWarning: true };

    if (noSiren) {
      return { errorMessage: KEYS.noSiren };
    }

    return {
      errorMessage: null,
      errorKey: null,
    };
  }, [
    displayNewAccountChoice,
    isLoading,
    cannotPay,
    noOwnerOrg,
    noSiren,
    noProductOwner,
    isResellee,
    hasWebHosting,
    KEYS.isReselleeWH,
    KEYS.cannotPay,
    KEYS.noOwnerOrg,
    KEYS.noSiren,
    KEYS.noProductOwner,
  ]);

  return { errorMessage, noWarning, isDisabled };
}
